import React, { useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import {
  COLORS,
  FONTSIZE,
  FONTWEIGHT
} from "helpers/designConstants";

const CatalogueViewerTOC = ({
  tocEntries,
  activeGroupIndex,
  onSelectGroup,
  seasonEntries,
  activeSeasonKey,
  onSeasonChange
}) => {
  const { i18n } = useTranslation();
  const itemRefs = useRef({});

  const setItemRef = useCallback((flatGroupIndex, el) => {
    if (el) {
      itemRefs.current[flatGroupIndex] = el;
    } else {
      delete itemRefs.current[flatGroupIndex];
    }
  }, []);

  // Scroll the active item into view when activeGroupIndex changes
  useEffect(() => {
    const el = itemRefs.current[activeGroupIndex];
    if (el) {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [activeGroupIndex]);

  return (
    <>
      {seasonEntries.length > 0 && (
        <SeasonPicker>
          <ul>
            {seasonEntries.map((season) => (
              <SeasonListItem
                key={season.key}
                isSelected={season.key === activeSeasonKey}
                onClick={() => onSeasonChange(season.key)}
              >
                {i18n.language === "en" ? season.name : season.name_fr}
              </SeasonListItem>
            ))}
          </ul>
        </SeasonPicker>
      )}
      <TOCContainer>
        {tocEntries.map((entry) => {
          const label = i18n.language === "en" ? entry.name : entry.name_fr;

          if (!entry.isLeaf) {
            return (
              <ParentEntry key={entry.key} depth={entry.depth}>
                {label}
              </ParentEntry>
            );
          }

          const isActive = entry.flatGroupIndex === activeGroupIndex;
          return (
            <LeafEntry
              key={entry.key}
              ref={(el) => setItemRef(entry.flatGroupIndex, el)}
              depth={entry.depth}
              $isActive={isActive}
              onClick={() => onSelectGroup(entry.flatGroupIndex)}
            >
              {label}
            </LeafEntry>
          );
        })}
      </TOCContainer>
    </>
  );
};

const SeasonPicker = styled.div`
  background: ${COLORS.tertiary};
  border-bottom: 1px solid ${COLORS.secondary};

  ul {
    margin: 0;
    padding: 0;
    list-style-type: none;
  }
`;

const SeasonListItem = styled.li`
  margin: 5px;
  padding: 10px;
  font-weight: ${FONTWEIGHT.heavy};
  font-size: ${FONTSIZE.medium};
  text-transform: uppercase;
  cursor: pointer;
  color: ${({ isSelected }) =>
    isSelected ? COLORS.primary : COLORS.secondary};
  background: ${({ isSelected }) => isSelected && COLORS.secondary};
  border-radius: 4px;
`;

const TOCContainer = styled.div`
  overflow-y: auto;
  padding: 8px 0;
`;

const ParentEntry = styled.div`
  padding: 6px 12px;
  padding-left: ${({ depth }) => depth * 16 + 12}px;
  font-size: ${FONTSIZE.small};
  font-weight: ${FONTWEIGHT.heavy};
  color: ${COLORS.text};
  text-transform: uppercase;
  user-select: none;
`;

const LeafEntry = styled.div`
  margin: 2px 8px;
  padding: 5px 10px;
  padding-left: ${({ depth }) => depth * 16 + 10}px;
  font-size: ${FONTSIZE.xsmall};
  text-transform: uppercase;
  cursor: pointer;
  border-radius: 3px;
  color: ${({ $isActive }) => ($isActive ? COLORS.primary : COLORS.text)};
  background: ${({ $isActive }) => ($isActive ? COLORS.secondary : "transparent")};
  font-weight: ${({ $isActive }) => ($isActive ? FONTWEIGHT.medium : FONTWEIGHT.medium)};

  @media (hover: hover) {
    &:hover {
      background: ${({ $isActive }) =>
        $isActive ? COLORS.secondary : COLORS.tertiary};
    }
  }
`;

export default CatalogueViewerTOC;
